/** Editor for the selected delivery method: enabled/theme header, a
 *  collapsible "Delivery settings" section (destination/flags — collapsed it
 *  shows a destination + active-flags summary; DeliverySettingsSection), the
 *  Standard|Custom rendering toggle for composition channels (STANDARD →
 *  layout picker + provenance; CUSTOM → per-field template accordions), plain
 *  template accordions for sns/sentry, and a read-only note for push (which
 *  has no console-editable settings). Composition channels can dock a
 *  resizable live-preview pane on the right (LivePreviewPane) — its open state
 *  persists in localStorage.
 */
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState, type MutableRefObject } from 'react';

import type {
  EditorMetadata,
  NotificationDeliveryMethod,
  NotificationDetail,
} from '@/api/notifications';
import { DELIVERY_METHODS, RENDER_CUSTOM, RENDER_STANDARD } from '@/api/notifications';
import { FieldHelp } from '@/components/FieldHelp';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import { NOTIFICATION_HELP } from '../fieldHelp';
import { themeActions } from '../notificationLinks';
import { PickerLinks } from '@/components/ValueActions/PickerLinks';
import type {
  BlockCompletionItem,
  ContextSchemaSource,
  TemplateCodeEditorHandle,
  TemplateDiagnostic,
} from '@/components/TemplateCodeEditor';

import type { ContextSourceValue } from '../test-render/ContextSourcePicker';
import type { NotificationDraft } from '../useNotificationDraft';
import { channelSpec } from './channelFields';
import { CustomTemplateAccordion, type FieldValidationState } from './CustomTemplateAccordion';
import { DeliverySettingsSection } from './DeliverySettingsSection';
import { methodLabel } from './DeliveryMethodList';
import { LivePreviewPane } from './LivePreviewPane';
import { StandardPathPanel } from './StandardPathPanel';

const PREVIEW_OPEN_KEY = 'nf-live-preview-open';

type ThemeOption = { id: number; codename: string; tour_operator?: number | null };

interface Props {
  dm: NotificationDeliveryMethod;
  detail: NotificationDetail;
  /** The page's lifted draft — the live preview builds its overrides from it. */
  draft: NotificationDraft;
  /** Page-lifted sticky render context (shared with TestRenderDialog). */
  renderContext: ContextSourceValue;
  onRenderContextChange: (v: ContextSourceValue) => void;
  canManage: boolean;
  /** Tenant-scoped themes from the editor metadata (undefined while loading). */
  themes: EditorMetadata['themes'] | undefined;
  blocks: BlockCompletionItem[];
  contextSchemas: ContextSchemaSource[];
  /** Live block-ref count from the draft (for the STANDARD provenance strip). */
  blockRefCount: number;
  /** Per-field diagnostics for THIS delivery method. */
  diagnostics: Record<string, TemplateDiagnostic[]> | undefined;
  /** Page-held validation state keyed `${dmId}.${field}`. */
  validation: Record<string, FieldValidationState>;
  onFieldChange: (dmId: number, field: string, value: unknown) => void;
  onValidationResult: (
    dmId: number,
    field: string,
    ok: boolean,
    diags: TemplateDiagnostic[],
  ) => void;
  onOpenTestRender: (dmId: number) => void;
  /** Opens the page's context-variables panel (reachable from the accordions). */
  onOpenContextPanel: () => void;
  focusRegistry: MutableRefObject<TemplateCodeEditorHandle | null>;
  /** Forwarded to each template accordion so a `?dm&field&line` deep link can
   *  find the right editor. */
  registerEditor?: (
    key: string,
    ref: MutableRefObject<TemplateCodeEditorHandle | null> | null,
  ) => void;
  /** The field a deep link is targeting, if it belongs to this method. */
  revealField?: string | null;
}

export function DeliveryMethodEditor({
  dm,
  detail,
  draft,
  renderContext,
  onRenderContextChange,
  canManage,
  themes,
  blocks,
  contextSchemas,
  blockRefCount,
  diagnostics,
  validation,
  onFieldChange,
  onValidationResult,
  onOpenTestRender,
  onOpenContextPanel,
  focusRegistry,
  registerEditor,
  revealField,
}: Props) {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const spec = channelSpec(dm.method);
  const isNew = dm.id < 0;

  // A docked pane needs room — below md the editor is already stacked, so the
  // live preview is a desktop-only affordance.
  const [previewOpen, setPreviewOpen] = useState(
    () => localStorage.getItem(PREVIEW_OPEN_KEY) === '1',
  );
  const togglePreview = () => {
    const next = !previewOpen;
    setPreviewOpen(next);
    try {
      localStorage.setItem(PREVIEW_OPEN_KEY, next ? '1' : '0');
    } catch {
      /* best effort */
    }
  };
  const previewAvailable = spec.composition && mdUp;

  // Theme options: tenant-scoped metadata themes, plus the current value as a
  // fallback option when it isn't in the list (cross-tenant/system edge).
  const themeOptions: ThemeOption[] = [...(themes ?? [])];
  if (dm.theme != null && !themeOptions.some((t) => t.id === dm.theme)) {
    const brief = detail.refs.themes[String(dm.theme)];
    themeOptions.unshift({ id: dm.theme, codename: brief?.codename ?? `#${dm.theme}` });
  }
  const themeValue = themeOptions.find((t) => t.id === dm.theme) ?? null;

  const renderingType = spec.renderingTypeField
    ? ((dm[spec.renderingTypeField] as number | undefined) ?? RENDER_STANDARD)
    : null;

  const renderAccordions = () => (
    <Stack spacing={1}>
      {spec.templateFields.map((field) => (
        <CustomTemplateAccordion
          key={field.field}
          notificationId={detail.id}
          dmId={dm.id}
          field={field}
          value={(dm[field.field] as string | null | undefined) ?? ''}
          event={detail.notification.event}
          tourOperator={detail.notification.tour_operator}
          canManage={canManage}
          diagnostics={diagnostics?.[field.field]}
          validationState={validation[`${dm.id}.${field.field}`]}
          contextSchemas={contextSchemas}
          blocks={blocks}
          onChange={(v) => onFieldChange(dm.id, field.field, v)}
          onValidationResult={(ok, diags) => onValidationResult(dm.id, field.field, ok, diags)}
          onOpenContextPanel={onOpenContextPanel}
          focusRegistry={focusRegistry}
          registerEditor={registerEditor}
          revealTarget={revealField === field.field}
        />
      ))}
    </Stack>
  );

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" color="primary" label={methodLabel(dm.method)} />
          <Typography variant="caption" color="text.secondary">
            {isNew ? (
              'new — created on save'
            ) : (
              <ValueActions actions={[act.copy(String(dm.id), 'Copy delivery-method id')]}>
                #{dm.id}
              </ValueActions>
            )}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={dm.enabled}
                disabled={!canManage}
                onChange={(e) => onFieldChange(dm.id, 'enabled', e.target.checked)}
              />
            }
            label={<Typography variant="body2">Enabled</Typography>}
          />
          <Box sx={{ width: 240 }}>
            <Autocomplete<ThemeOption>
              size="small"
              options={themeOptions}
              value={themeValue}
              disabled={!canManage}
              onChange={(_, v) => onFieldChange(dm.id, 'theme', v?.id ?? null)}
              getOptionLabel={(t) => `${t.codename}${t.tour_operator == null ? ' (system)' : ''}`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField {...params} label="Theme" placeholder="Operator default" />
              )}
            />
            {themeValue && (
              <PickerLinks
                items={[
                  { key: themeValue.id, label: themeValue.codename, actions: themeActions(themeValue) },
                ]}
              />
            )}
          </Box>
          {spec.composition && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
              {previewAvailable && (
                <Tooltip title="Docked live preview — re-renders this method as you edit.">
                  <Button
                    size="small"
                    startIcon={<VisibilityOutlinedIcon />}
                    color={previewOpen ? 'primary' : 'inherit'}
                    onClick={togglePreview}
                  >
                    Live preview
                  </Button>
                </Tooltip>
              )}
              <Tooltip
                title={
                  isNew ? 'Save the notification first — test render needs a persisted method.' : ''
                }
              >
                <span>
                  <Button
                    size="small"
                    startIcon={<PlayArrowIcon />}
                    disabled={isNew}
                    onClick={() => onOpenTestRender(dm.id)}
                  >
                    Test render this method
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          )}
        </Stack>

        <DeliverySettingsSection
          key={dm.id}
          dm={dm}
          tenantId={detail.notification.tour_operator}
          canManage={canManage}
          onFieldChange={onFieldChange}
        />

        {spec.composition && spec.renderingTypeField ? (
          <>
            <Box>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={renderingType === RENDER_STANDARD ? 'standard' : 'custom'}
                disabled={!canManage}
                onChange={(_, v: 'standard' | 'custom' | null) => {
                  if (!v || !spec.renderingTypeField) return;
                  onFieldChange(
                    dm.id,
                    spec.renderingTypeField,
                    v === 'standard' ? RENDER_STANDARD : RENDER_CUSTOM,
                  );
                }}
              >
                <ToggleButton value="standard">Standard</ToggleButton>
                <ToggleButton value="custom">Custom</ToggleButton>
              </ToggleButtonGroup>
              <FieldHelp help={NOTIFICATION_HELP.renderingType} />
            </Box>
            {renderingType === RENDER_STANDARD ? (
              <StandardPathPanel
                dm={dm}
                spec={spec}
                detail={detail}
                blockRefCount={blockRefCount}
                canManage={canManage}
                onTemplateChange={(templateId) => {
                  if (spec.templateField) onFieldChange(dm.id, spec.templateField, templateId);
                }}
              />
            ) : (
              renderAccordions()
            )}
          </>
        ) : spec.templateFields.length > 0 ? (
          renderAccordions()
        ) : dm.method === DELIVERY_METHODS.PUSH_NOTIFICATION ? (
          <Alert severity="info">
            Push notifications have no console-editable settings — configure them in the operator
            dashboard.
          </Alert>
        ) : null}
      </Stack>

      {previewAvailable && previewOpen && (
        <LivePreviewPane
          detail={detail}
          draft={draft}
          dm={dm}
          source={renderContext}
          onSourceChange={onRenderContextChange}
          onClose={togglePreview}
        />
      )}
    </Box>
  );
}
